import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Badge } from '@repo/ui/badge';
import { DataTable } from '@repo/ui/data-table';
import { formatDate } from '@repo/ui/lib/formatters';
import { Skeleton } from '@repo/ui/skeleton';
import { TableCell } from '@repo/ui/table';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import type { RouterOutputs } from '@/providers/trpc-client';
import { OfficersTableActions } from './officers-table-actions';
import { OfficersTableEmptyState } from './officers-table-empty-state';
import { OfficersTableErrorState } from './officers-table-error-state';
import type { OfficersTableFiltersProps } from './officers-table-filters';
import { OfficersTableFilters } from './officers-table-filters';

interface OfficersTableProps extends Omit<OfficersTableFiltersProps, 'hasFilters'> {
  data: RouterOutputs['organization']['listOrganizationMembers']['data'];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRefetch: () => void;
}

export function OfficersTable({
  data,
  isLoading,
  isError,
  error,
  onRefetch,
  filters,
  onFilterChange,
  onClearFilters,
}: OfficersTableProps) {
  useLocale();

  const columns = useMemo<
    ColumnDef<RouterOutputs['organization']['listOrganizationMembers']['data'][number]>[]
  >(
    () => [
      {
        get header() {
          return t('workspace.name');
        },
        cell: ({ row }) => {
          return (
            <div className="font-medium text-sm">
              {row.original.user.firstName} {row.original.user.lastName}
            </div>
          );
        },
      },
      {
        get header() {
          return t('reports.email');
        },
        cell: ({ row }) => {
          return (
            <div className="text-muted-foreground text-sm">{row.original.user.email || 'N/A'}</div>
          );
        },
      },
      {
        accessorKey: 'role',
        get header() {
          return t('workspace.role');
        },
        cell: ({ row }) => {
          const role = row.original.role;
          return <Badge variant="outline">{role ? String(role) : 'N/A'}</Badge>;
        },
      },
      {
        get header() {
          return t('reports.status');
        },
        cell: ({ row }) => {
          if (row.original.joinedAt === null) {
            return <Badge variant="outline">{t('workspace.pendingInvitation')}</Badge>;
          }
          return (
            <Badge variant={row.original.user.disabled ? 'default' : 'secondary'}>
              {row.original.user.disabled ? t('workspace.inactive') : t('workspace.active')}
            </Badge>
          );
        },
      },
      {
        get header() {
          return t('workspace.createdAt');
        },
        cell: ({ row }) => {
          return (
            <div className="whitespace-nowrap text-muted-foreground text-sm">
              {formatDate(row.original.user.createdAt)}
            </div>
          );
        },
      },
      {
        id: 'actions',
        get header() {
          return t('reports.actions');
        },
        cell: ({ row }) => {
          return (
            <OfficersTableActions
              isPendingInvitation={row.original.joinedAt === null}
              officerId={row.original.user.id}
            />
          );
        },
      },
    ],
    [getLocale()],
  );

  return (
    <div className="space-y-4">
      <OfficersTableFilters
        filters={filters}
        hasFilters={!!filters.search || filters.role !== 'ALL'}
        onClearFilters={onClearFilters}
        onFilterChange={onFilterChange}
      />
      <DataTable
        columns={columns}
        data={data}
        emptyState={
          <OfficersTableEmptyState
            hasFilters={!!filters.search || filters.role !== 'ALL'}
            onClearFilters={onClearFilters}
            searchQuery={filters.search}
          />
        }
        error={{
          enable: isError || false,
          component: (
            <TableCell className="h-32 text-center" colSpan={columns.length}>
              <OfficersTableErrorState error={error} onRefetch={onRefetch} />
            </TableCell>
          ),
        }}
        skeleton={{
          enable: isLoading,
          rows: 5,
          component: columns.map((_, colIndex) => (
            <TableCell key={`skeleton-col-${colIndex}`}>
              <Skeleton className="h-8 w-full" />
            </TableCell>
          )),
        }}
      />
    </div>
  );
}
